import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import DatePicker from './DatePicker';

const API = process.env.REACT_APP_API_URL || '';

const FORMAT_THRESHOLDS = { single: 1, ep: 6 }; // ≤1 = Single, 2-6 = EP, 7+ = Full Length

function UploadStep({ onComplete }) {
  const [dragging, setDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef();

  // Project details collected before upload
  const [projectName, setProjectName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [releaseDate, setReleaseDate] = useState('');

  const handleFileSelected = useCallback((file) => {
    if (!file || !file.name.toLowerCase().endsWith('.zip')) {
      setError('Please select a .zip file');
      return;
    }
    setError('');
    setSelectedFile(file);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFileSelected(e.dataTransfer.files[0]);
  }, [handleFileSelected]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onInputChange = (e) => handleFileSelected(e.target.files[0]);

  const canSubmit = selectedFile && projectName.trim() && productCode.trim() && releaseDate;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;

    setError('');
    setUploading(true);
    setProgress(0);
    setStage('Uploading...');

    const formData = new FormData();
    formData.append('zipfile', selectedFile);
    formData.append('projectName', projectName.trim());
    formData.append('productCode', productCode.trim().toUpperCase());
    formData.append('releaseDate', releaseDate);

    try {
      const res = await axios.post(`${API}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded / (e.total || 1)) * 60);
          setProgress(pct);
          if (pct < 20) setStage('Uploading...');
          else if (pct < 50) setStage('Extracting zip...');
          else setStage('Parsing credits...');
        },
        timeout: 0
      });

      setProgress(80);
      setStage('Processing artwork...');
      await new Promise(r => setTimeout(r, 300));
      setProgress(100);
      setStage('Done');

      setTimeout(() => onComplete(res.data.sessionId, res.data), 300);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
      setUploading(false);
      setProgress(0);
    }
  }, [canSubmit, selectedFile, projectName, productCode, releaseDate, onComplete]);

  return (
    <Container>
      <Inner>
        <Eyebrow>Pinkfong · Deliverable intake</Eyebrow>
        <Title>New build</Title>
        <Subtitle>Drop your deliverable and fill in the project details</Subtitle>

        {/* Drop zone — always visible, file can be changed before submit */}
        <DropZone
          dragging={dragging}
          hasFile={!!selectedFile}
          uploading={uploading}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !uploading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={onInputChange}
          />

          {uploading ? (
            <ProgressArea>
              <ProgressLabel>{stage}</ProgressLabel>
              <ProgressBar><ProgressFill pct={progress} /></ProgressBar>
              <ProgressPct>{progress}%</ProgressPct>
            </ProgressArea>
          ) : selectedFile ? (
            <FileReady>
              <FileIcon>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect x="5" y="3" width="18" height="22" rx="2" stroke="var(--accent)" strokeWidth="1.5"/>
                  <path d="M9 9h10M9 13h10M9 17h6" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </FileIcon>
              <FileDetails>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>{formatBytes(selectedFile.size)}</FileSize>
              </FileDetails>
              <ChangeFile onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
                Change
              </ChangeFile>
            </FileReady>
          ) : (
            <>
              <DropIcon dragging={dragging}>
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <rect x="6" y="10" width="24" height="20" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M12 10V8a2 2 0 012-2h8a2 2 0 012 2v2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M18 16v8M15 19l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </DropIcon>
              <DropText>{dragging ? 'Release to select' : 'Drop .zip file here'}</DropText>
              <DropSub>or click to browse — up to 10GB</DropSub>
            </>
          )}
        </DropZone>

        {/* Project details form — always shown */}
        <FormCard>
          <FormTitle>Project Details</FormTitle>
          <FormGrid>
            <FormField>
              <FormLabel>Project Name <Req>*</Req></FormLabel>
              <FormInput
                type="text"
                placeholder="e.g. Pinkfong Panda Songs"
                value={projectName}
                onChange={e => setProjectName(e.target.value)}
                disabled={uploading}
              />
            </FormField>

            <FormField>
              <FormLabel>Product Code <Req>*</Req></FormLabel>
              <FormInput
                type="text"
                placeholder="e.g. PF341"
                value={productCode}
                onChange={e => setProductCode(e.target.value.toUpperCase())}
                disabled={uploading}
                mono
              />
              <FormHint>Language codes will be appended automatically (PF341EN, PF341ES…)</FormHint>
            </FormField>

            <FormField full>
              <FormLabel>Release Date <Req>*</Req></FormLabel>
              <DatePicker
                value={releaseDate}
                onChange={setReleaseDate}
                disabled={uploading}
                placeholder="Select release date"
              />
              <FormHint>Used for both Release Date and Sale Start Date</FormHint>
            </FormField>
          </FormGrid>
        </FormCard>

        {error && <ErrorMsg>⚠ {error}</ErrorMsg>}

        <SubmitBtn
          onClick={handleSubmit}
          disabled={!canSubmit || uploading}
          ready={canSubmit && !uploading}
        >
          {uploading ? 'Processing...' : 'Process Deliverable →'}
        </SubmitBtn>
      </Inner>
    </Container>
  );
}

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// ── Styled components ──────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: calc(100vh - 67px);
  padding: 64px 24px;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Eyebrow = styled.p`
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--accent-text);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 14px;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: 44px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: -0.7px;
  line-height: 1.08;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: var(--text-2);
  font-size: 14.5px;
  margin-bottom: 36px;
`;

const DropZone = styled.div`
  width: 100%;
  min-height: ${p => p.hasFile ? '80px' : '200px'};
  border: 1.5px dashed ${p => p.dragging ? 'var(--accent)' : p.hasFile ? 'var(--accent-border)' : 'var(--border-2)'};
  border-radius: var(--radius-lg);
  background: ${p => p.dragging ? 'var(--accent-dim)' : p.hasFile ? 'var(--accent-dim)' : 'var(--bg-2)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: ${p => p.uploading ? 'default' : 'pointer'};
  transition: all 0.15s;
  margin-bottom: 16px;
  padding: ${p => p.hasFile ? '18px 22px' : '32px'};

  &:hover {
    border-color: ${p => p.uploading ? (p.hasFile ? 'var(--accent-border)' : 'var(--border-2)') : 'var(--accent)'};
    background: ${p => p.uploading ? (p.hasFile ? 'var(--accent-dim)' : 'var(--bg-2)') : 'var(--accent-dim)'};
  }
`;

const FileReady = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FileIcon = styled.div`flex-shrink: 0;`;

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.p`
  font-size: 12px;
  color: var(--accent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
`;

const FileSize = styled.p`
  font-size: 10px;
  color: var(--text-3);
  font-family: var(--font-mono);
`;

const ChangeFile = styled.button`
  background: transparent;
  color: var(--text-3);
  font-size: 11px;
  padding: 4px 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  flex-shrink: 0;
  &:hover { color: var(--text); border-color: var(--border-2); }
`;

const DropIcon = styled.div`
  color: ${p => p.dragging ? 'var(--accent)' : 'var(--text-3)'};
  transition: color 0.15s;
`;

const DropText = styled.p`
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: -0.3px;
`;

const DropSub = styled.p`
  font-size: 11px;
  color: var(--text-3);
`;

const ProgressArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
`;

const ProgressLabel = styled.p`
  font-size: 12px;
  color: var(--text-2);
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 3px;
  background: var(--border);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${p => p.pct}%;
  background: var(--accent);
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const ProgressPct = styled.p`
  font-size: 11px;
  color: var(--text-3);
`;

const FormCard = styled.div`
  width: 100%;
  background: var(--bg-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 24px;
  margin-bottom: 16px;
`;

const FormTitle = styled.h3`
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: var(--text-3);
  margin-bottom: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  grid-column: ${p => p.full ? '1 / -1' : 'auto'};
`;

const FormLabel = styled.label`
  font-size: 10px;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: 0.7px;
  display: flex;
  align-items: center;
  gap: 3px;
`;

const Req = styled.span`color: var(--accent); font-size: 11px;`;

const FormInput = styled.input`
  background: var(--bg-3);
  border: 1px solid var(--border);
  color: var(--text);
  padding: 9px 12px;
  border-radius: var(--radius);
  font-size: 13px;
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'var(--font-ui)'};
  font-weight: ${p => p.mono ? '400' : '500'};
  transition: border-color 0.1s;
  width: 100%;

  &:focus { border-color: var(--accent); background: var(--bg-4); outline: none; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &::placeholder { color: var(--text-3); font-weight: 400; font-family: var(--font-mono); }
`;

const FormHint = styled.p`
  font-size: 10px;
  color: var(--text-3);
  line-height: 1.4;
`;

const ErrorMsg = styled.div`
  width: 100%;
  padding: 10px 14px;
  background: var(--red-dim);
  border: 1px solid var(--red);
  border-radius: var(--radius);
  color: var(--red);
  font-size: 12px;
  margin-bottom: 12px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 15px;
  background: ${p => p.ready ? 'var(--accent)' : 'var(--bg-3)'};
  color: ${p => p.ready ? '#ffffff' : 'var(--text-3)'};
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 14.5px;
  border-radius: var(--radius);
  border: 1px solid ${p => p.ready ? 'transparent' : 'var(--border)'};
  transition: all 0.15s;
  cursor: ${p => p.ready ? 'pointer' : 'not-allowed'};
  &:hover { opacity: ${p => p.ready ? '0.9' : '1'}; }
`;

export default UploadStep;
